import { Component, OnInit } from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { RegistroService } from 'src/app/registro.service';
import { generarMenu } from './utils/menu.utils';
import { MenuItem } from './interface/menu-item.interface';
import { filter } from 'rxjs/operators';
import { combineLatest } from 'rxjs';
import { AccessControlService } from 'src/app/access-control/services/access-control.service';
import { esUsuarioInstitucional as esEmailInstitucional } from 'src/app/access-control/utils/usuario-institucional.util';
import { CaracterizacionComercializador } from 'src/interfaces/caracterizacion-comercializador.interface';

/**
 * Componente del panel lateral izquierdo que muestra el menú de navegación
 *
 * Este componente se encarga de mostrar un menú dinámico basado en el rol del usuario
 * y sus permisos. El menú se actualiza automáticamente cuando cambian las condiciones
 * del usuario.
 *
 * @example
 * <app-leftpanel></app-leftpanel>
 *
 * @since 1.0.0
 */
@Component({
    selector: 'app-leftpanel',
    templateUrl: './leftpanel.component.html',
    styleUrls: ['./leftpanel.component.css'],
})
export class LeftpanelComponent implements OnInit {
    /**
     * Indica si el usuario está registrado en el sistema
     * @default true
     */
    registrado = true;

    /**
     * Indica si el usuario tiene empresas asociadas
     * @default true
     */
    empresasAsociadas = true;

    /**
     * Habilita las opciones de comercializadores
     * @default false
     */
    opcionesComercializadores = false;

    /**
     * Rol del usuario actual
     */
    rolUsuario: string;

    /**
     * Array de items del menú que se mostrarán en el panel
     */
    menuItems: MenuItem[] = [];

    /**
     * Indica si el usuario está en la página de selección de empresas
     */
    enPaginaCargarEmpresas = false;

    /**
     * Indica si el usuario MADR tiene permisos para acceder al menú
     */
    tienePermisos = false;

    /**
     * Estado de clasificación del comercializador
     */
    estadoClasificacion: string;

    /**
     * Correo del usuario autenticado
     */
    userEmail: string | null = null;

    /**
     * Indica si el usuario pertenece a un dominio institucional (UPRA/MADR)
     */
    get esUsuarioInstitucional(): boolean {
        return esEmailInstitucional(this.userEmail);
    }

    /**
     * Crea una instancia del componente LeftpanelComponent
     *
     * @param router - Servicio de routing para navegación
     * @param registroService - Servicio para gestionar el estado del registro
     */
    constructor(
        public router: Router,
        private readonly registroService: RegistroService,
        public readonly accessControlService: AccessControlService
    ) {}

    /**
     * Inicializa el componente y suscribe a los observables del servicio
     *
     * Este método se ejecuta cuando el componente es inicializado y se suscribe
     * a los cambios en el estado del registro, rol del usuario y eslabones de empresa.
     *
     * @remarks
     * Las suscripciones se gestionan automáticamente por Angular
     */
    async ngOnInit() {
        this.enPaginaCargarEmpresas = this.router.url === '/cargar-empresas';

        // Suscribirse a cambios de ruta
        this.router.events
            .pipe(filter((event) => event instanceof NavigationEnd))
            .subscribe((event: NavigationEnd) => {
                this.enPaginaCargarEmpresas = event.url === '/cargar-empresas';
                this.actualizarMenu();
            });

        // Suscripción al estado de registro
        this.registroService.registrado$.subscribe((registrado) => {
            this.registrado = registrado;
        });

        // Suscripción al estado de empresas asociadas
        this.registroService.empresasAsociadas$.subscribe((empresaAsociada) => {
            this.empresasAsociadas = empresaAsociada;
            this.actualizarMenu();
        });

        // Suscripción al rol del usuario
        this.registroService.rolUsuario$.subscribe((rol) => {
            this.rolUsuario = rol;
            this.actualizarMenu();
        });

        this.registroService.representanteAgenteVigilado$.subscribe(() => {
            this.tienePermisos = this.accessControlService.validarPermisos();
            this.userEmail = this.accessControlService.obtenerEmailUsuario();
            this.actualizarMenu();
        });

        this.userEmail = this.accessControlService.obtenerEmailUsuario();

        // El menú se deriva SIEMPRE de la empresa realmente seleccionada
        // (cruzando el id seleccionado con la lista de empresas asociadas),
        // no de un estado suelto que puede sobrescribirse al crear/caracterizar
        // otra empresa. Así, crear una empresa comercializadora nueva mientras
        // se tiene seleccionada una productora/importadora no voltea el menú.
        combineLatest([
            this.registroService.empresaSelected$,
            this.registroService.listaEmpresasAsociadas$,
            this.registroService.eslabonEmpresaSelected$,
            this.registroService.caracterizacionComercializador$,
        ]).subscribe(
            ([
                empresaSelectedId,
                listaEmpresas,
                eslabonFallback,
                caracterizacionComercializador,
            ]) => {
                const empresaSeleccionada = listaEmpresas?.find(
                    (empresa) =>
                        String(empresa.id) === String(empresaSelectedId)
                );
                const caracterizacion: CaracterizacionComercializador =
                    caracterizacionComercializador as CaracterizacionComercializador | null;
                this.estadoClasificacion = caracterizacion?.estadoClasificacion;
                // Si la empresa está en la lista usamos su eslabón real; si aún no
                // se ha cargado, caemos al eslabón almacenado como respaldo.
                const eslabon = empresaSeleccionada?.eslabon ?? eslabonFallback;
                this.opcionesComercializadores = !!this.validarEslabon(eslabon);
                this.actualizarMenu();
            }
        );
    }

    /**
     * Valida si los eslabones de empresa permiten mostrar opciones de comercializador
     *
     * @param eslabones - Cadena de eslabones separados por '/'
     * @returns `true` si incluye 'Comercializador' y todos los eslabones están permitidos
     *
     * @example
     * const resultado = this.validarEslabon('Comercializador/Empacador');
     * // resultado: true
     */
    validarEslabon(eslabones: string): boolean {
        if (!eslabones) {
            // console.warn('No hay eslabones en el localStorage');
            return;
        }
        const permitidos = new Set([
            'Comercializador',
            'Empacador',
            'Envasador',
        ]);
        const partes = eslabones.split('/').map((e) => e.trim());
        return (
            partes.includes('Comercializador') &&
            partes.every((e) => permitidos.has(e))
        );
    }

    /**
     * Actualiza los items del menú basándose en el rol y permisos actuales
     *
     * Este método utiliza la función `generarMenu` para crear la estructura
     * del menú según las condiciones actuales del usuario.
     */
    actualizarMenu() {
        this.menuItems = generarMenu(
            this.rolUsuario,
            this.opcionesComercializadores,
            this.enPaginaCargarEmpresas,
            this.empresasAsociadas,
            this.estadoClasificacion,
            this.userEmail
        );
    }

    /**
     * Navega a la ruta especificada
     *
     * @param route - Ruta a la que se desea navegar
     *
     * @example
     * this.navegar('/dashboard');
     */
    navegar(route: string) {
        this.router.navigate([route]);
    }

    /**
     * Indica si el usuario tiene acceso al ítem del menú, evaluando
     * `permissions` (varios códigos válidos, basta con tener uno). Si el
     * ítem no define ninguno, siempre es visible.
     *
     * @param item - Ítem del menú a validar
     * @returns `true` si el ítem debe mostrarse
     *
     * @example
     * this.tieneAccesoItem({ label: 'Cumplimiento', permissions: ['a', 'b'] });
     */
    tieneAccesoItem(item: MenuItem): boolean {
        const codigos = item.permissions ?? [];

        if (!codigos.length) {
            return true;
        }

        return this.accessControlService.tieneAlgunoDeLosPermisos(codigos);
    }
}
